import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { scheduleKeys } from './queryKeys';
import { mapShiftRpcError } from '@/hooks/useSchedulingMutations';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
}

interface CreateShiftArgs {
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
}

export const useCreateShift = (
  companyId: string | undefined,
  currentWeek: Date,
  employees: Profile[]
) => {
  const queryClient = useQueryClient();
  const weekStartISO = startOfWeek(currentWeek).toISOString();
  const weekEndISO = endOfWeek(currentWeek).toISOString();
  
  return useMutation({
    mutationFn: async (args: CreateShiftArgs) => {
      const rpcArgs: any = {
        p_start: args.start_time,
        p_end: args.end_time,
        p_note: args.note
      };
      
      // Only include p_profile_id if it's not null (omit when null for TypeScript compatibility)
      if (args.profile_id) {
        rpcArgs.p_profile_id = args.profile_id;
      }

      console.log('[Frontend] Calling create_shift RPC with:', rpcArgs);

      const { data, error } = await supabase.rpc('create_shift', rpcArgs);

      if (error) {
        console.error('[Frontend] Error from create_shift RPC:', error);
        throw error;
      }

      return { data, args };
    },
    
    onMutate: async (args) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO)
      });
      
      // Snapshot previous value
      const previousShifts = queryClient.getQueryData(
        scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO)
      );
      
      // Create optimistic shift
      const selectedEmployeeData = args.profile_id ? employees.find(emp => emp.id === args.profile_id) : null;
      const employeeName = args.profile_id === null 
        ? 'Open shift'
        : selectedEmployeeData 
          ? `${selectedEmployeeData.first_name} ${selectedEmployeeData.last_name}`.trim()
          : '—';

      const optimisticShift = {
        id: `temp-${Date.now()}`,
        profile_id: args.profile_id,
        start_time: args.start_time,
        end_time: args.end_time,
        note: args.note,
        status: args.profile_id === null ? 'open' : 'active',
        employee_name: employeeName,
        company_id: companyId
      };
      
      // Optimistically update cache
      queryClient.setQueryData(
        scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
        (old: any[]) => [...(old || []), optimisticShift]
      );
      
      return { previousShifts, optimisticShift };
    },
    
    onSuccess: ({ data, args }, _, context) => {
      // Replace optimistic shift with real data
      const realShiftId = typeof data === 'string' ? data : data?.id;
      
      if (realShiftId && context?.optimisticShift) {
        queryClient.setQueryData(
          scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
          (old: any[]) => (old || []).map(shift => 
            shift.id === context.optimisticShift.id 
              ? { ...context.optimisticShift, id: realShiftId }
              : shift
          )
        );
      }
      
      toast.success('Shift created successfully');
    },
    
    onError: (error: any, args, context) => {
      // Rollback optimistic update
      if (context?.previousShifts) {
        queryClient.setQueryData(
          scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
          context.previousShifts
        );
      }
      
      const errorMessage = mapShiftRpcError(error.message);
      toast.error(errorMessage || 'Failed to create shift');
    },
    
    onSettled: (data, error, args) => {
      // Always refetch to ensure consistency across all schedule views
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.all
      });
    }
  });
};
